import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, ChevronRight, ShieldCheck, Send, Eye } from 'lucide-react';

const STEPS = [
  {
    num: '01',
    tag: 'Takes 2 minutes',
    title: 'Create your message',
    desc: 'Write the questions you\'ve always wanted to ask. Set up a vibe check, opinion poll, or whatever you want to know — then add their contact details.',
    icon: <Eye className="w-5 h-5" style={{ color: '#e11d48' }} />,
  },
  {
    num: '02',
    tag: 'Your identity stays hidden',
    title: 'Yhuu delivers it anonymously',
    desc: 'We send it to them via WhatsApp, Snapchat, or Telegram — with no trace back to you. They have no idea who sent it.',
    icon: <Send className="w-5 h-5" style={{ color: '#e11d48' }} />,
  },
  {
    num: '03',
    tag: 'AI-powered analysis',
    title: 'See the results',
    desc: 'Their answers show up in your space. Yhuu highlights mixed signals and unexpected responses.',
    icon: <ShieldCheck className="w-5 h-5" style={{ color: '#e11d48' }} />,
  },
];

interface OnboardingModalProps {
  onClose: () => void;
  // Fired only when the person taps the final "Get Started" button — takes
  // them straight into the create-a-session flow. Falls back to onClose if
  // not provided.
  onGetStarted?: () => void;
}

export default function OnboardingModal({ onClose, onGetStarted }: OnboardingModalProps) {
  const [step, setStep] = useState(0);
  const [exiting, setExiting] = useState(false);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  function handleClose() {
    setExiting(true);
    setTimeout(() => onClose(), 300);
  }

  function handleGetStarted() {
    setExiting(true);
    setTimeout(() => (onGetStarted ? onGetStarted() : onClose()), 300);
  }

  const isLast = step === STEPS.length - 1;
  const current = STEPS[step];

  return (
    <AnimatePresence>
      {!exiting && (
        <motion.div
          className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-16"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0"
            style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }}
            onClick={handleClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          />

          {/* Modal — same card style as the "Send Anonymously Now!" box */}
          <motion.div
            className="relative w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl"
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.97 }}
            transition={{ type: 'spring', stiffness: 340, damping: 30 }}
          >
            {/* Gradient banner header = step title */}
            <div
              className="relative px-14 py-7 text-center"
              style={{ background: 'linear-gradient(90deg, #e11d48, #fb7185)' }}
            >
              <AnimatePresence mode="wait">
                <motion.p
                  key={step}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.18 }}
                  className="text-white font-black text-xl sm:text-2xl tracking-tight leading-tight"
                >
                  {current.title}
                </motion.p>
              </AnimatePresence>
              <button
                onClick={handleClose}
                aria-label="Close"
                className="absolute top-1/2 -translate-y-1/2 right-4 w-9 h-9 rounded-full flex items-center justify-center bg-white/30 active:scale-95 transition-transform"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>

            {/* White body */}
            <div className="bg-white px-8 pt-10 pb-8 min-h-[340px] flex flex-col text-center">
              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.22, ease: 'easeOut' }}
                  className="flex flex-col flex-1 items-center"
                >
                  {/* Step number + icon */}
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-11 h-11 rounded-2xl flex items-center justify-center bg-rose-50 border border-rose-100">
                      {current.icon}
                    </div>
                    <span className="font-mono text-3xl font-bold leading-none text-rose-300">
                      {current.num}
                    </span>
                  </div>

                  {/* Description — big and bold like the card headline */}
                  <h3 className="text-[22px] sm:text-2xl font-black leading-[1.3] tracking-tight text-zinc-900 flex-1">
                    {current.desc}
                  </h3>

                  {/* Tag */}
                  <span className="mt-6 inline-block text-xs font-bold px-4 py-1.5 rounded-full bg-rose-50 border border-rose-200 text-rose-600 tracking-wide">
                    {current.tag}
                  </span>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Button + dots */}
            <div className="bg-white px-8 pb-8">
              <button
                onClick={isLast ? handleGetStarted : () => setStep(s => s + 1)}
                className="w-full font-black text-base py-5 rounded-full flex items-center justify-center gap-2 text-white transition-transform active:scale-[0.97]"
                style={{ background: 'linear-gradient(90deg, #e11d48, #ec4899)' }}
              >
                {isLast ? 'Get Started' : 'Next'}
                <ChevronRight className="w-5 h-5" />
              </button>

              <div className="flex items-center justify-center gap-2 mt-5">
                {STEPS.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setStep(i)}
                    aria-label={`Go to step ${i + 1}`}
                    className="rounded-full transition-all duration-300"
                    style={{
                      width: i === step ? 24 : 8,
                      height: 8,
                      background: i === step ? '#e11d48' : '#fecdd3',
                    }}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
